import { FLYING_DESIGN_TRANSFER_CONFIG, FLYING_UPLOADER_TRANSFER_API_PATH } from './design-transfer.constants';
import type {
	IFlyingDesignTransferAckRequest,
	IFlyingDesignTransferCreateRequest,
	IFlyingDesignTransferExchangeRequest,
	IFlyingDesignTransferPairingRequest,
	TypeFlyingDesignTransferImageMime,
	TypeFlyingDesignTransferStatus
} from './design-transfer.interface';


const INVALID_REQUEST_MESSAGE = 'Invalid design transfer request';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const INVALID_FLY_ID_PATTERN = /[^A-Za-z0-9_-]/;
const GRANT_PATTERN = /^[0-9a-f]{64}$/;
const CONTROL_CHARACTER_PATTERN = /[\p{Cc}\p{Cf}]/u;


function validateRequestKeys<TKey extends string>(value: unknown, keys: readonly TKey[]): Record<TKey, unknown> {
	if (typeof value !== 'object' || value === null || Array.isArray(value)) throw new Error(INVALID_REQUEST_MESSAGE);
	const prototype: unknown = Object.getPrototypeOf(value);
	if (prototype !== Object.prototype && prototype !== null) throw new Error(INVALID_REQUEST_MESSAGE);
	const actualKeys = Object.keys(value);
	if (actualKeys.length !== keys.length || actualKeys.some((key) => !keys.some((expected) => expected === key))) throw new Error(INVALID_REQUEST_MESSAGE);
	return value as Record<TKey, unknown>;
}

export function validateFlyingDesignTransferUuid(value: unknown): string {
	if (typeof value !== 'string' || value.length !== 36 || !UUID_PATTERN.test(value)) throw new Error(INVALID_REQUEST_MESSAGE);
	return value.toLowerCase();
}

export function validateFlyingDesignTransferFlyId(value: unknown): string {
	if (typeof value !== 'string' || value.length === 0 || value.length > FLYING_DESIGN_TRANSFER_CONFIG.maxFlyIdLength || INVALID_FLY_ID_PATTERN.test(value)) throw new Error(INVALID_REQUEST_MESSAGE);
	return value;
}

export function validateFlyingDesignTransferGrant(value: unknown): string {
	if (typeof value !== 'string' || value.length !== 64 || !GRANT_PATTERN.test(value)) throw new Error(INVALID_REQUEST_MESSAGE);
	return value;
}

export function validateFlyingDesignTransferDeviceLabel(value: unknown): string {
	if (typeof value !== 'string' || CONTROL_CHARACTER_PATTERN.test(value)) throw new Error(INVALID_REQUEST_MESSAGE);
	const label = value.trim();
	if (label.length === 0 || label.length > FLYING_DESIGN_TRANSFER_CONFIG.maxDeviceLabelLength) throw new Error(INVALID_REQUEST_MESSAGE);
	return label;
}

export function validateFlyingDesignTransferPairingRequest(value: unknown): IFlyingDesignTransferPairingRequest {
	const request = validateRequestKeys(value, ['label']);
	return { label: validateFlyingDesignTransferDeviceLabel(request.label) };
}

export function validateFlyingDesignTransferExchangeRequest(value: unknown): IFlyingDesignTransferExchangeRequest {
	const request = validateRequestKeys(value, ['grant']);
	return { grant: validateFlyingDesignTransferGrant(request.grant) };
}

export function validateFlyingDesignTransferCreateRequest(value: unknown): IFlyingDesignTransferCreateRequest {
	const request = validateRequestKeys(value, ['deviceId', 'requestId', 'flyIds']);
	const deviceId = validateFlyingDesignTransferUuid(request.deviceId);
	const requestId = validateFlyingDesignTransferUuid(request.requestId);
	if (!Array.isArray(request.flyIds) || request.flyIds.length === 0 || request.flyIds.length > FLYING_DESIGN_TRANSFER_CONFIG.maxDesigns) throw new Error(INVALID_REQUEST_MESSAGE);
	const flyIds = Array.from(request.flyIds, (flyId: unknown) => validateFlyingDesignTransferFlyId(flyId));
	if (new Set(flyIds.map((flyId) => flyId.toLowerCase())).size !== flyIds.length) throw new Error(INVALID_REQUEST_MESSAGE);
	return { deviceId, requestId, flyIds };
}

export function validateFlyingDesignTransferAckRequest(value: unknown): IFlyingDesignTransferAckRequest {
	const request = validateRequestKeys(value, ['status']);
	if (request.status !== 'received') throw new Error(INVALID_REQUEST_MESSAGE);
	return { status: 'received' };
}

export function createFlyingDesignTransferImageFileName(flyId: string, mimeType: TypeFlyingDesignTransferImageMime): string {
	const safeFlyId = validateFlyingDesignTransferFlyId(flyId);
	if (mimeType === 'image/png') return `design-${safeFlyId}.png`;
	if (mimeType === 'image/jpeg') return `design-${safeFlyId}.jpg`;
	throw new Error(INVALID_REQUEST_MESSAGE);
}

export function getFlyingDesignTransferImagePath(transferId: string, flyId: string): string {
	const safeTransferId = validateFlyingDesignTransferUuid(transferId);
	const safeFlyId = validateFlyingDesignTransferFlyId(flyId);
	return `${FLYING_UPLOADER_TRANSFER_API_PATH}/transfers/${safeTransferId}/images/${safeFlyId}`;
}

/** A terminal result cannot be reversed. Acknowledgement replay remains valid after expiry until the record is purged. */
export function getFlyingDesignTransferNextStatus(current: TypeFlyingDesignTransferStatus, next: 'received' | 'cancelled'): TypeFlyingDesignTransferStatus {
	if (next !== 'received' && next !== 'cancelled') throw new Error(INVALID_REQUEST_MESSAGE);
	if (current === 'queued' || current === next) return next;
	throw new Error(INVALID_REQUEST_MESSAGE);
}

/** Expiry blocks new processing, including a download started exactly at expiresAt. */
export function isFlyingDesignTransferExpired(expiresAt: string, now: number): boolean {
	const expiry = Date.parse(expiresAt);
	if (!Number.isFinite(expiry) || !Number.isFinite(now) || new Date(expiry).toISOString() !== expiresAt) throw new Error(INVALID_REQUEST_MESSAGE);
	return now >= expiry;
}
